/* eslint-disable react-hooks/exhaustive-deps */
// import "bootstrap-daterangepicker/daterangepicker.css";
import PropTypes from 'prop-types';
import { useCallback, useState, useEffect } from 'react';
import DatePicker from "react-datepicker";
import { sentenceCase } from '@repo/design-system/components/forms/utils';
import "react-datepicker/dist/react-datepicker.css";
import "./DatePicker.scss";
import "./DateRange.scss";

const DatePickerComponent = ({
    selected,
    onChange,
    placeholder,
    onBlur,
    isDisabled,
    datePickerConfig
}) => {

    const [endRange, setEndRange] = useState(null);
    const [startRange, setStartRange] = useState(null);

    const parseToDate = (d) => {
        if (!d) return null;
        if (d instanceof Date) return d;
        if (typeof d === 'string') {
            const parts = d.split('T')[0].split('-');
            if (parts.length === 3) {
                const newDate = new Date(parts[0], parts[1] - 1, parts[2]);
                return newDate
            }
        }
        const parsed = new Date(d);
        return isNaN(parsed.getTime()) ? null : parsed;
    };

    useEffect(() => {
        if (datePickerConfig?.isRangePicker || datePickerConfig?.singleInputRangePicker || datePickerConfig?.isSingleRangePicker) {
            if (Array.isArray(selected)) {
                setStartRange(parseToDate(selected[0]));
                setEndRange(parseToDate(selected[1]));
            } else if (selected) {
                setStartRange(parseToDate(selected));
                setEndRange(null);
            } else {
                setStartRange(null);
                setEndRange(null);
            }
        }
    }, [selected, datePickerConfig]);

    const parsedSelected = Array.isArray(selected) ? null : parseToDate(selected);

    const [inputAttributes] = useState({});

    const configMapping = {
        format: "dateFormat",
        timeFormat: "timeFormat",
        timeIntervals: "timeIntervals",
        isMonthPicker: "showMonthYearPicker",
        displayInline: "inline",
        minDate: "minDate",
        maxDate: "maxDate",
        startDate: "openToDate",
        displayWeekNumber: "showWeekNumbers",
        selectWeekDates: "showWeekPicker",
        enableTodayBtn: "todayButton",
        highlightDates: "highlightDates",
        locale: "locale",
        calPropUpPosition: "popperPlacement",
        allowClear: "isClearable",
        showIcon: "showIcon",
        icon: "icon",
        holidays: "holidays",
        displayYearddl: "showYearDropdown",
        noOfYearinDdl: "yeardisplayinDropDown",
        noOfYear: "yearItemNumber",
        enableDdlScroll: "scrollabelYearDropdown",
        inline: "inline",

        // Css Related Configuration
        className: "className", // class name for calender
        timeClassName: "timeClassName", // class name for Time Input
        calendarClassName: "calendarClassName", // class name for Menu
        dayClassName: "dayClassName", // class name for days

        // Excluding
        excludeDates: "excludeDates",
        excludeDateIntervals: "excludeDateIntervals", // Exclude date intervals

        // Range configuration
        isSingleRangePicker: "selectsRange",

        // Time Input configurations
        displayTimeInput: "showTimeInput",
        displayTimeSelect: "showTimeSelect",
        displayTimeSelectOnly: "showTimeSelectOnly",

        // Month configuration
        displayMonthPicker: "showMonthYearPicker",
        displayFullMonthName: "showFullMonthYearPicker",
        displayDoubleColumnMonthPicker: "showTwoColumnMonthYearPicker",
        displayMonthDropdown: "showMonthDropdown",
        displayShortMonthDropdown: "useShortMonthInDropdown",
        displayMonthYearDropdown: "showMonthYearDropdown",
        multiMonthsCount: "monthsShown",
        displayPreviousMonth: "showPreviousMonths",

        // Year configurations
        displayYearPicker: "showYearPicker",

        // Quarter configuration
        displayQuarterPicker: "showQuarterYearPicker",
        renderCustomHeader: "renderCustomHeader",
        renderDayContents: "renderDayContents",
        renderMonthContent: "renderMonthContent",
        renderQuarterContent: "renderQuarterContent",
        renderYearContent: "renderYearContent"
    };

    const dynamicAttributes = Object.entries(configMapping).reduce((acc, [configKey, propKey]) => {
        if (datePickerConfig?.[configKey] !== undefined) {
            acc[propKey] = datePickerConfig[configKey];
        }
        return acc;
    }, {});

    const handleDateChange = useCallback((newValue) => {
        if (datePickerConfig?.isRangePicker || datePickerConfig?.singleInputRangePicker || datePickerConfig?.isSingleRangePicker) {
            const [start, end] = newValue;
            setStartRange(start);
            setEndRange(end);
        }
        if (onChange) {
            onChange(newValue);
        }
    }, [onChange, datePickerConfig?.isRangePicker, datePickerConfig]);

    return (
        <div className={`date-picker ${datePickerConfig?.isMonthPicker ? 'month-picker-only' : ''}`}>
            <DatePicker
                placeholderText={sentenceCase(placeholder)}
                selected={datePickerConfig?.isRangePicker ? startRange : parsedSelected}
                onChange={handleDateChange}
                onBlur={onBlur}
                autoComplete="off"
                disabled={isDisabled}
                startDate={datePickerConfig?.isRangePicker ? startRange : undefined}
                endDate={datePickerConfig?.isRangePicker ? endRange : undefined}
                selectsRange={datePickerConfig?.isRangePicker || datePickerConfig?.isSingleRangePicker}
                showMonthDropdown={datePickerConfig?.showMonthDropdown ?? true}
                showYearDropdown={datePickerConfig?.showYearDropdown ?? true}
                dropdownMode="select"
                scrollableYearDropdown={datePickerConfig?.scrollableYearDropdown ?? true}
                yearDropdownItemNumber={datePickerConfig?.yearDropdownItemNumber ?? 100}
                dateFormat={datePickerConfig?.format ?? "dd/MM/yyyy"}
                {...dynamicAttributes}
                {...inputAttributes}
                popperProps={{ strategy: "fixed" }}
                className={`input-field ${datePickerConfig?.className || ''}`} />
        </div>
    );
};

DatePickerComponent.propTypes = {
    selected: PropTypes.instanceOf(Date),
    onChange: PropTypes.func.isRequired,
    placeholder: PropTypes.string,
    onBlur: PropTypes.func,
    isDisabled: PropTypes.bool,
    datePickerConfig: PropTypes.object
};

export default DatePickerComponent;